export default {
  profile: {
    north: 'Forested',
    south: 'Prairie',
    topNav: {
      prev: 'Prev',
      next: 'Next',
      guildHeader: 'Conservation Status',
      dataPeriod: 'Data Collected',
      resultYear: 'Results updated',
      dataModel: 'Data Summary'
    },
    aboutSpp: {
      header: 'About the {sppName}',
      habitat: {
        title: 'Habitat',
        description: 'Where the {sppName} can be found:'
      },
      description: {
        title: 'Description',
        description: 'What the {sppName} looks like:'
      },
      occurrenceLinkText: 'Occurrence',
      mapHeader: 'Occurrence in Alberta',
      mapInterpretHeader: 'Interpreting Maps',
      mapSummary: '',
      mapDescription:
        `
        The map shows the ABMI sites where the {sppName} has been detected between {startYear} and {endYear}.
        `,
      plantMapDescription: `
        {sppName} is associated with wetland habitats; therefore the occurrence map includes detections from both terrestrial ABMI sites and wetland ABMI sites.
      `,
      natureLynx: {
        header: 'Photo Gallery',
        link: 'Photos taken by citizen scientists across Alberta',
        noSighting: 'Be the first one to submit a sighting of {sppName}'
      }
    },
    ABStatus: {
      banner: {
        otherTaxon: `The ABMI monitors the status of the {speciesGroup} in Alberta.
          These data are used to examine how the abundance of the {speciesGroup} changes in relation to native land cover, human footprint,
          and spatial/climate variables.`,
        birds: `The ABMI and partners monitor the status of the {speciesGroup} in Alberta.
          These data are used to examine how the abundance of the {speciesGroup} changes in relation to native land cover, human footprint, and spatial/climate variables.`
      },
      sectionIntroLead: 'In this section we present:',
      species: {
        aboutHeader: 'Status of {sppName} in Alberta',
        withoutContent: 'The {sppName} was detected too infrequently to analyze habitat associations; therefore only species occurrence in Alberta is presented.',
        model: {
          full: `The {sppName} was detected enough times to model how species relative
          abundance changed in response to vegetation types (in the forested region), soil types (in the prairie region)
          and human footprint types (both regions).`,
          prairie: `The {sppName} was detected enough times to model how species relative abundance changed in response to soil types
                and human footprint types in the {regionType} region.`,
          forest: `The {sppName} was detected enough times to model how species relative abundance changed in response to vegetation types
                and human footprint types in the {regionType} region.`,
          basic: 'The {sppName} had few detections; therefore, only a coarse index of habitat use is presented in this section.'
        },
      },
      habitat: {
        aboutHeader: ' Summary of {sppName} in Alberta',
        descripton: ` Model results for {sppName} are shown below including its distribution in
        Alberta and associations with habitat types and human footprint types.`,
        model: `Model results for {sppName} are shown below including its distribution in Alberta and associations with habitat 
        types and human footprint types.`
      },
      habSuitability: {
        sectionHeader: 'Habitat Suitability',
        currentMapTitle: 'Current condition',
        referenceMapTitle: 'Reference condition',
        sliderTip: 'Use slider <span class="compare-swiper-vertical-icon"></span> to compare maps</h3>',
        compareMap: {
          header: 'Habitat Suitability',
          interpretHeader: 'Interpreting habitat suitability maps',
          description: `
          <ul>
          <li>The current condition shows the distribution of suitable habitat for {sppName}
          in the current landscape taking human footprint (circa {mapYear}) into account.
          Move the slider  to the right (→) to view the Current Predicted Relative Abundance map.
          </li>
          <li>The reference condition shows the distribution of suitable habitat for {sppName} if
          there was no human footprint. Move the slider  to the left (←) to view the Reference Predicted Relative Abundance map.
          </li>
          <li>
          Red pixels are predicted to have the most suitable habitat, grading through light tan to dark blue where the species is predicted to be less abundant or absent.
          </li>
          </ul>
          `,
          detail: `
            <ul>
            <li>
            Habitat association models, plus models describing how species varied spatially and with climate gradients were used to predict habitat suitability (relative species abundance) in 1 km<sup>2</sup> spatial units under current conditions and reference conditions.
            </li>
            <li>
            Predictions of habitat suitability (relative species abundance) were made using vegetation and human footprint present in the 1 km<sup>2</sup> unit under current conditions based on the latest Human Footprint Inventory, in this case, {mapYear}.
            </li>
            <li>
            Predictions of relative abundance for reference conditions were made when all human footprint had been “backfilled” based on native vegetation in the surrounding area.
            </li>
            <li>
            Predicted relative abundance estimates are normalized before displaying to range from 0% to 100%.
            </li>
            <li>
            These maps have uncertainty due to uncertainty in the models and in the underlying vegetation map.
            </li>
            </ul>
            `,
          legend: {
            low: 'Low Habitat Suitaiblity',
            high: 'High Habitat Suitaiblity'
          }
        },

        differentMap: {
          label: 'Difference',
          interpretHeader: 'Interpreting difference map',
          description: `
          The difference map shows areas where habitat suitability for the {sppName} is predicted to increase or decrease as a result of human footprint.
          `,
          header: 'Change in Habitat Suitability',
          detail: `
            <ul>
            <li>
            The difference map shows where habitat suitability is predicted to be better or worse when comparing current conditions to references conditions in each 1 km² unit.
            </li>
            <li>
            In the difference map, green indicates habitat suitability is improved under current conditions than under reference conditions, with the opposite true for 1 km² units depicted in pink. The intensity of green and pink represent the relative magnitude of increase or decrease for the species between current and reference conditions.
            </li>
            </ul>`,
          legend: {
            low: 'Decreased Habitat Suitaiblity',
            high: 'Increased Habitat Suitaiblity'
          }
        }
      },
      habHFAssociation: {
        sectionHeader: 'Habitat Associations',
        vegChart: {
          species: {
            header: 'Species-habitat Associations in the Forested Region',
            caption: `<u>Figure: Forested Region - Habitat Associations.</u> Predicted species relative abundance (bars) as a function of vegetation and human footprint type in the forested region. 
            Dots are added to forest types where harvesting occurs and show the predicted species abundance in harvested stands of various ages. Vertical lines represent 90% confidence intervals.`
          },
          habitat: {
            header: 'Habitat Associations in the Forested Region: {sppName}',
            caption: `<u>Figure: Forested Region - Habitat Associations.</u> Predicted values of {sppName} (bars) as a function of vegetation and human footprint type in the forested region. 
            Dots are added to forest types where harvesting occurs and show the predicted values in harvested stands of various ages. Vertical lines represent 90% confidence intervals. `
          }
        },

        treedChart: {
          sectionHeader: 'Species-habitat Associations in the Prairie Region',
          header: '{chartType} Sites in the Prairie Region',
          caption: '<u>Figure: Prairie Region - Habitat Associations.</u> Predicted species relative abundance (bars) in each soil type and human footprint type in the prairie region. Vertical lines indicate 90% confidence intervals. The presence/absence of trees greatly affects the presence and abundance of many species; therefore, separate figures are presented for treed and non-treed sites in the prairie region.',
          autoFigure: '{sppName} relative abundance is {moreLess} at treed sites compared to non-treed sites in the prairie region.',

        },
        vegFewChart: {
          header: 'Habitat Associations for Species with Few Detections in the {regionType} Region',
          yAxis: 'Relative Abundance',
          caption: `<u> Figure: {regionType} Region - Habitat Use-Availability Index.</u>
          Index of species habitat use based on the proportion of species detections in each native vegetation and human footprint type in comparison to the habitat availability.
          The index (bars) range from -1 (avoidance) to +1 (preference),
          given availability of a particular vegetation or human footprint type.`,
          birdNote2023: 'Data included in this analysis were collected from 1997-2019.'

        }
      },
      impactHF: {
        sectionHeader: 'Impacts of Human Footprint',
        subSectionHeader: 'Human Footprint Effects in the {regionType} Region',
        local: {
          header: 'Local Scale Effects',
          caption: '<u>Figure: {regionType} Region - Local Scale Effects.</u> Predicted changes to {sppName} relative abundance inside areas that have been disturbed by each sector (human footprint type) compared to the habitat it replaced (modelled reference condition with no human footprint). Sector effect values less than 0% indicate habitat suitability is reduced (predicted related abundance is lower) compared to reference conditions, and values more than 0% indicate habitat suitability is improved (predicted relative abundance is higher) compared to reference conditions.'
        },
        regional: {
          header: 'Regional Population Effects',
          caption: '<u>Figure: {regionType} Region - Regional Population Effects.</u> Predicted change in the total regional population by industrial sector for  {sppName}. This incorporates the area of the footprint, the native habitats where the footprint occurs, and the species response to a particular footprint. Regional population effect values less than 0% indicate a predicted decrease in the regional population due to a particular sector’s footprint, and values greater than 0% indicate a predicted increase.'
        },
        autoFigure: {
          sectorEffect: {
            all: {
              default: '',
              increase: `All human footprint types improve habitat suitability for {sppName} in the {regionType} region—abundance is higher in
                    all human footprint categories compared to the habitat each footprint replaces.`,
              mixed: `<li>In the {regionType} region, {increaseListHFType} footprints improve habitat suitability for {sppName};
                  abundance is higher in these footprint types compared to the habitat each footprint replaces.</li>
                  <li>The {decreaseListHFType} footprints decrease habitat suitability for {sppName};
                  abundance is lower in these footprint types compared to the habitat each footprint replaces.`,
              majorIncrease: `<li>{sppName} is more abundant in all human footprint categories, except {decreaseListHFType},
                  compared to the habitat each footprint replaces in the {regionType} region.</li>`,
              majorDecrease: `<li>{sppName} is less abundant in all human footprint categories, except {increaseListHFType},
                compared to the habitat each footprint replaces in the {regionType} region.</li>`,
              decrease: `All human footprint types decrease habitat suitability for {sppName} in the {regionType} region—abundance is
                  lower in all human footprint categories compared to the habitat each footprint replaces.`
            },
            // regional: {
            //   increase: {
            //     overall: `In the {regionType} region, the regional population of {sppName} is more abundant than expected compared to
            //     reference conditions as a result of {increaseListHFType} footprint.`,
            //     second: 'The effects of the remaining industrial sectors on the regional population of the {sppName} are small.',
            //     mixed: `<li>In the {regionType} region, {increaseListHFType} improve habitat suitability for {sppName};
            //     abundance is higher in these footprint types compared to the habitat each footprint replaces.</li>
            //     <li>{decreaseListHFType} decrease habitat suitability for {sppName}; abundance is lower in these footprint types
            //     compared to the habitat each footprint replaces.</li>`,
            //     majorIncrease: `{sppName} is more abundant in all human footprint categories, except {decreaseListHFType},
            //         compared to the habitat each footprint replaces in the {regionType} region.`,
            //       decrease: `{sppName} is less abundant in all human footprint categories, except {increaseListHFType},
            //       compared to the habitat each footprint replaces in the {regionType} region.`
            //     }
            // },
            // smallChange: 'In the {regionType}, changes in the regional population of {sppName} are small due to all industrial sectors.'
            // },
          },
        },
        linearHFChart: {
          sectionHeader: 'Linear Footprint Effects',
          header: 'Effects of Linear Footprint in the {regionType} Region',
          yAxis: 'Under Footprint Effect (%)',
          caption: `
          <u>Figure: Linear Footprint - Local Scale Effects.</u>
          Predicted changes to {sppName} relative abundance inside areas that have been disturbed by each linear feature compared to
          the habitat it replaced (modelled reference condition with no linear footprint).
          Footprint effect values less than 0% indicate that habitat suitability is reduced (predicted related abundance is lower) compared to reference conditions,
          and values more than 0% indicate that habitat suitability is improved (predicted relative abundance is higher) compared to reference conditions.
          Linear footprint categories are defined as: Energy Seismic = wide and narrow seismic lines; Energy Soft Linear = pipeline and transmission lines;
          Transportation Soft Linear = road and rail verges; Transportation Hard Linear = roads and rails.
          `, // <u>Figure: {regionType} Region - Relationship to Linear Footprint.</u> Species relative abundance predicted for habitat with no human footprint compared to habitat in which 10% of the area is converted to either soft or hard linear footprint.',
          autoFigure: {
            // negetive: '{sppName} has a negative relationship with hard and soft linear footprint in the {regionType} region.',
            // noRelation: '{sppName} has no relationship with hard and soft linear footprint in the {regionType} region.',
            // positive: '{sppName} has a positive relationship with hard and soft linear footprint in the {regionType} region.',
            // negetivePostive: '{sppName} has a negative relationship with hard linear footprint and a positive relationship with soft linear footprint in the {regionType} region.',
            // postiveNegetive: '{sppName} has a positive relationship with hard linear footprint and a negative relationship with soft linear footprint in the {regionType} region.'
            // <li>The largest increases in habitat suitability are in {leadIncreaseHF} footprint, followed by {otherIncreaseHF} footprints.
            // </li>  <li>The largest decreases in habitat suitability are in {leadDecreaseHF} footprint, followed by {otherDecreaseHF} footprints.
            // </li>
            allIncrease: `<ul><li>All linear footprint types improve habitat suitability for {sppName} in the {regionType}
            region—abundance is higher in
                all linear footprint categories compared to the habitat each footprint replaces.</li></ul>
                `,
            allDecrease: `<ul><li>All linear footprint types decrease  habitat suitability for {sppName} in the {regionType} region—abundance is lower in
              all linear footprint categories compared to the habitat each footprint replaces.</li></ul>
              `,
            equal: `
            <ul><li>
            In the {regionType} region, {increaseListHFType} footprints improve habitat suitability for {sppName};
            abundance is higher in these footprint types compared to the habitat each footprint replaces.
            </li>
            <li>
            The {decreaseListHFType} footprints decrease habitat suitability for {sppName};
            abundance is lower in these footprint types compared to the habitat each footprint replaces.
            </ul>
            `,
            moreIncrease: `<ul><li>{sppName} is more abundant in all linear footprint categories, except {decreaseListHFType},
            compared to the habitat each footprint replaces in the {regionType} region.</li></ul>`,
            moreDecrease: `<ul><li>{sppName} is less abundant in all linear footprint categories, except {increaseListHFType},
            compared to the habitat each footprint replaces in the {regionType} region.</li></ul>`,
          }
        },
        // linearHFBarChart: {
        //   sectionHeader: 'Relationship to Linear Footprint',
        //   header: 'Relationship to Linear Footprint in the {regionType} Region',
        //   yAxis: 'Under Footprint Effect (%)',
        //   caption: `
        //   <u>Figure: {regionType} Region - Relationship to Linear Footprint.</u>
        //   Predicted changes to {sppName} relative abundance inside areas that have been disturbed by each linear feature compared to the habitat it replaced
        //   (modelled reference condition with no human footprint).
        //   Footprint effect values less than 0% indicate that habitat suitability is reduced (predicted related abundance is lower)
        //   compared to reference conditions, and values more than 0% indicate that habitat suitability is improved (predicted relative abundance is higher)
        //   compared to reference conditions. Linear footprint categories are defined as follows:
        //   Energy Seismic = wide and narrow seismic lines;
        //   Energy Soft Linear = pipeline and transmission lines;
        //   Transportation Soft Linear = road and rail verges;
        //   Hard Linear = roads and rails.
        //   `,
        //   autoFigure: {
        //     negetive: '{sppName} has a negative relationship with hard and soft linear footprint in the {regionType} region.',
        //     noRelation: '{sppName} has no relationship with hard and soft linear footprint in the {regionType} region.',
        //     positive: '{sppName} has a positive relationship with hard and soft linear footprint in the {regionType} region.',
        //     negetivePostive: '{sppName} has a negative relationship with hard linear footprint and a positive relationship with soft linear footprint in the {regionType} region.',
        //     postiveNegetive: '{sppName} has a positive relationship with hard linear footprint and a negative relationship with soft linear footprint in the {regionType} region.'
        //   }
        // },
      },
      // current: 'Current',
      // ref: 'Reference',
      // diff: 'Difference',
      // currentMap: 'The current condition is the predicted relative abundance of {sppName} taking current human footprint (circa {year}) into account.',
      // refMap: 'The reference condition shows the predicted relative abundance of {sppName}  after all human footprint had been backfilled based on native vegetation in the surrounding area.',
      // diffMap: 'The difference map shows areas where the current relative abundance of {sppName} is predicted to be higher or lower compared to reference conditions. In other words, where habitat suitability is predicted to increase or decrease as a result of human footprint.'
    },
    OSRBirdStatus: {
      banner: `The ABMI and partners monitors the status of {sppName} in the Oil Sands Region.
        These data are used to examine how the abundance of the {sppName} changes in relation to energy-specific footprints,
        such as seismic lines and well sites. `,

      introSection: {
        title: 'About {sppName} Status in the Oil Sands Region',
        description: `The {sppName} is commonly detected in the Oil Sands Region.
        We modelled how species relative abundance changed in response to energy-specific human footprint types including:
        seismic lines, pipelines, mines sites, industrial sites, and well sites.
        `
      },
      osrOccurrenceSection: {
        sectionHeader: 'Occurrence in the Oil Sands Region',
        sectionShortHeader: 'Occurrence in the OSR',
        header: 'Occurrence in the Oil Sands Region',
        description: `The map shows the ABMI site where {sppName} has been detected between
        {mapStartYear} and {mapEndYear} in the Oil Sands Region.
        <br>High quality habitat is indicated by areas in red in the occurrence map.
        `
      },
      osrHfImpactSection: {
        sectionHeader: 'Impacts of Energy-Specific Human Footprint',
        sectionShortHeader: 'Energy Footprint Distribution',
        title: 'Distribution of Energy-specific Human Footprint in the OSR',
        description: `The total area of human footprint in the Oil Sands Region (circa {mapYear}) was {totalHF}%. Of that area, energy-specific footprint covered {energyHF}% of the region, including:
          <ul style="text-align:left">
          <li>Seismic lines → {seismic}%</li>
          <li>Pipelines → {pipelines}%</li>
          <li>Mine sites → {mine}%</li>
          <li>Industrial facilities → {industrial}%</li>
          <li>Well sites → {well}% (note: the human footprint map displays both active and abandoned well sites but these categories were combined in the analysis)</li>
          </ul>
        `,
        figure: `
          Figure: Map of Human Footprint.
          Map of total human footprint and five energy-specific footprint types in the Oil
          Sands Region, circa {mapYear}.
          Click on footprint categories in the legend to turn layers on and off.
          Zoom into the map for a detailed view of the distribution of human footprint.
        `
      },
      osrHfTabSection: {
        sectionHeader: 'Energy-specific Effects on {sppName}',
        sectionShortHeader: 'Energy-specific Effects',
        subTitle: `Impacts of {hfType} on {sppName}:
          What We Know`,
        local: {
          header: 'Local-scale Effects',
          figure: `<u>Figure: Local Scale Effects.</u>
            Predicted changes to {sppName} relative abundance inside areas that have been disturbed by each energy
            footprint type compared to the habitat it replaced (modelled reference condition with no human footprint).
            Sector effect values less than 0% indicate habitat suitability is reduced (predicted related abundance is lower)
            compared to reference conditions, and values more than 0% indicate habitat suitability is improved (predicted relative abundance is higher)
            compared to reference conditions.`
        },
        regional: {
          header: 'Regional Population Effects',
          figure: `<u>Figure: Regional Population Effects.</u>
          Predicted change in the total regional population by energy footprint type for {sppName}.
          This incorporates the area of the footprint, the native habitats where the footprint occurs,
          and the species response to each footprint.
          Regional population effect values less than 0% indicate a predicted decrease in the regional population due to a particular sector's footprint,
          and values greater than 0% indicate a predicted increase.
          `
        }
      }
    },
    OSRMammalStatus: {
      banner: `The ABMI monitors the potential impacts of oil sands development activities in the Oil Sands Region of Alberta as part of the Oil Sands Monitoring program.
          Alberta as part of the Oil Sands Monitoring program.
          Results show how the abundances of mammal species are changing in response to human footprint of the energy industry.`,

      introSection: {
        withContent: 'In this section we present:',
        title: 'About {sppName} Status in the Oil Sands Region',
        description: `We summarized abundance of {sppName} in six treatments in the Oil Sands Region comparing: 
          1) abundance on energy-related human footprint (HF) compared to off footprint, 
          and 2) abundance with increasing distance from energy footprint.
  
          `
      },
      methods: `<p>The Oil Sands Monitoring program (OSM) surveys mammals using cameras in six treatments.[Bayne et al. 2022]:</p>
      <ol style = "padding-left: 40px;"><li>Reference: Areas with no oil sands development. </li>
      <li>Dense linear features: Areas where grids of seismic lines have been used for exploration. Cameras are on the seismic lines (dense linear on-HF) and in adjacent vegetation (dense linear off-HF).</li>
      <li>Low-density well sites: Areas with exploratory well sites, most currently inactive. Cameras are on the well sites (low-density wellpads on-HF) and in adjacent vegetation (low-density wellpads off-HF).</li>
      <li>High-density in situ: Areas with active in situ extraction. Cameras are on the well sites and pipelines (high-density in situ on-HF), and in adjacent vegetation (high-density in situ off-HF).</li>
      <li>Roads: Roads associated with oil sands activities. Cameras are in the adjacent vegetation at 10m, 30m, 100m, and 300m from the road.</li>
      <li>Processing plants or mine sites: Cameras are in the adjacent vegetation at 10m, 30m, 100m, and 300m from the perimeter of the facility.</li></ol>
      <p>Each treatment is surveyed in two vegetation types: 1) deciduous forest of 40+ years, and 2) lowland forest of 20+ years.
      Additionally, ABMI has conducted surveys using the same camera methods throughout the oil sands area. 
      These cameras were added to the OSM dataset to increase sample sizes, especially for reference areas.</p>
      <p>Images taken by the movement-triggered cameras were converted to estimated abundances of mammal species using a method based on the time animals spend in the camera field-of-view and the area surveyed by each camera [Becker et al. 2022]. 
      We summarized abundances of six common mammal species for the six treatments, 
      averaging the two vegetation types—including separate estimates on versus off human footprint (HF) (treatments 2-4) or 
      at different edge distances (treatments 5-6).</p>
      <p>Estimates of mammal abundances at cameras are highly variable, leading to wide uncertainty in some treatments. 
      We used a smoothing method to reduce the variability in treatment results.
      The method is designed so that the smoothing has less effect as more data accumulate
      and estimates for each treatment become more reliable with ongoing sampling</p>
      <p>Results will be available for more mammal species in coming years.</p>`,
      OnvsOffFootprint: {
        sectionHeader: 'On vs Off Footprint',
        header: 'Occurrence in the Oil Sands Region',
        description: `The map shows the ABMI site where {sppName} has been detected between
        {mapStartYear} and {mapEndYear} in the Oil Sands Region.
        <br>High quality habitat is indicated by areas in red in the occurrence map.
        `
      },
      DistancefromEdge: {
        sectionHeader: 'Distance from Edge',
        sectionShortHeader: 'Distance from Edge',
        title: 'Distribution of Energy-specific Human Footprint in the OSR',
        description: `The total area of human footprint in the Oil Sands Region (circa {mapYear}) was {totalHF}%. Of that area, energy-specific footprint covered {energyHF}% of the region, including:
          <ul style="text-align:left">
          <li>Seismic lines → {seismic}%</li>
          <li>Pipelines → {pipelines}%</li>
          <li>Mine sites → {mine}%</li>
          <li>Industrial facilities → {industrial}%</li>
          <li>Well sites → {well}% (note: the human footprint map displays both active and abandoned well sites but these categories were combined in the analysis)</li>
          </ul>
        `,
        figure: `
          Figure: Map of Human Footprint.
          Map of total human footprint and five energy-specific footprint types in the Oil
          Sands Region, circa {mapYear}.
          Click on footprint categories in the legend to turn layers on and off.
          Zoom into the map for a detailed view of the distribution of human footprint.
        `
      },
      onOffChart: {
        sectionHeader: 'On Versus Off Human Footprint Treatments',
        results: `We used a simple overall mean of {sppName} density for the four off-human footprint (HF) treatments (reference, dense linear off-HF, 
        low-density wellpads off-HF, high-density in situ off-HF), and a separate mean for the three on-HF treatments (dense linear on-HF, low-density wellpads on-HF, high-density in situ on-HF). 
        `,
        caption: `Figure: Abundance for Edge-Distance Treatments. Mean relative abundance of {sppName} in four off-human footprint treatments in the Oil Sands Region, 
        including: Reference, OFF dense linear footprint, OFF low-density wellpads, and OFF high activity in situ. 
        Means for three on-footprint treatments include: ON dense linear footprint, ON low-density wellpads, and ON high activity in situ.
        Vertical lines represent 90% confidence intervals.
        `,
      },
      edgeDistanceChart: {
        sectionHeader: 'Distance from Edge',
        header1: 'Roads associated with oil sands activities',
        header2: 'Processing plants or mines',
        yAxis: 'Relative Distance',
        xAxis: 'Distance from {chartType}',
        caption: `
        Figure: Abundance for Edge-Distance Treatments. Relative abundance of {sppName} estimated at four distances (10 m, 30, m, 100 m, 300 m)
        from the edge of human footprint in the Oil Sands Region.Densities were measured in two treatments: roads associated with oil sands activities and processing plants or mines. 
        Vertical lines represent 90% confidence intervals. 
        `,
        results: `We estimated abundances of {sppName} with increasing distances (10 m, 30 m, 100 m, 300 m) from the edge of:
        <ul>
        <li style = "list-style-type: disc;">roads associated with oil sands activities </li>
        <li style = "list-style-type: disc;">processing plants or mines</li>
        </ul>`
      },
    },
    referenceSection: {
      title: 'References & Credits',
      reference: {
        title: 'References',
      },
      source: {
        title: 'Data Source',
        groupBird: `
            <p>ABMI bird point count data were combined with data from other organizations and individuals to develop the bird species models:
              </p>
              <ul>
                <li>Environment Canada (North American Breeding Bird Survey
                  and Joint Oil Sands Monitoring programs)</li>
                <li>Ecological Monitoring Committee for the Lower Athabasca
                  (EMCLA)</li>
                <li>Dr. Erin Bayne (University of Alberta)</li>
                <li>Raw data are available for download:<a href="//abmi.ca/home/data-analytics/da-top/da-product-overview/Species-Habitat-Data.html"target="blank"> ABMI Species and Habitat Data.</a></li>
              </ul>
              `,
        groupOther: '<p>Data collected by ABMI.</p>'
      },
      photo: {
        title: 'Photo Credits',
        placeHolder: '<p>Photos: TBD</p>',
        category: {
          banner: 'Banner',
          characteristics: 'Description',
          habitat: 'Habitat',
          profile: 'Profile',
        }
      },
      citation: {
        title: 'Recommended Citation',
        content: `<p>
              Alberta Biodiversity Monitoring Institute{birdContent}. {citationYear}.
              {sppFullName}.
              ABMI Website: <a id="external_link"
                href="https://abmi.ca{url}">
                https://abmi.ca{url}</a>.
            </p>
          </div>`,
        birdContent: ' and Boreal Avian Modelling Project'
      },
      resource: {
        title: 'Additional ABMI Resources',
        content: `<div id="additional_resources">
            <p>
              Alberta Biodiversity Monitoring Institute. 2016. ABMI Species
              Website Manual, Version: 2016-12-02. Alberta Biodiversity
              Monitoring Institute, Alberta, Canada. Report available at: <a
                href="//abmi.ca/home/publications/401-450/443.html"
                target="blank">abmi.ca</a>.
            </p>

            <p>
              Alberta Biodiversity Monitoring Institute. 2014. Manual for
              Species Modeling and Intactness, Version 2014-09-25. Alberta
              Biodiversity Monitoring Institute, Alberta, Canada. Report
              available at: <a
                href="//abmi.ca/home/publications/1-50/47.html"
                target="blank">abmi.ca</a>.
            </p>
            <p>
              Alberta Biodiversity Monitoring Institute. 2014. Terrestrial field
              data collection protocols (abridged version) 2016-05-18. Alberta
              Biodiversity Monitoring Institute, Alberta, Canada. Report
              available at: <a
                href="//abmi.ca/home/publications//401-450/432.html"
                target="blank">abmi.ca</a>.
            </p>
          </div>`
      },
      content: {
        title: 'Content Contributor'
      }
    }
  },
  search: {
    backButton: 'Back to Introduction',
    wetland: {
      label: 'Wetland',
      description: `<h5> Wetland Indicator Status</h5>
      <div>
      This species is associated with wetlands in Alberta. Open species profile to view wetland indicator status.
      </div>`
    },

    input: {
      message: 'Search for a Species in the Table'
    },
    filter: 'Advanced filters',
    resultInfo: 'Showing {filteredRecordNo} of {totalRecordNo}',
    resultInfoWithTerms: 'Showing {filteredRecordNo} of {totalRecordNo} for "{filterTerm}"',
    filterTerms: ' ',
    columns: {
      name: 'Species Name',
      cname: 'Common Name',
      sname: 'Scientific Name',
      summary: 'Summary',
      wetland: 'Wetland',
      tag: 'Tags',
      osr: 'OSR'
    },
    tooltips: {
      Full: `<div style="max-width: 300px">
          <span class="summary-icon full">Full Summary:</span> The ABMI has detected the species enough times in the northern analysis unit (forested region) and the southern analysis unit (prairie region) to model how species relative abundance differed among vegetation, soil and, human footprint types for both these regions.</div>`,
      Forested: '<div style="max-width: 300px"><span class="summary-icon forested">Forested Region Summary:</span>The ABMI has detected the species enough times in the northern analysis unit (i.e. forested region) to model how species relative abundance differed among vegetation and human footprint types for the forested region only. A coarse index of habitat use may be available for prairie region.</div>',
      Prairie: '<div style="max-width: 300px"><span class="summary-icon prairie">Prairie Region Summary:</span> The ABMI has detected the species enough times in the southern analysis unit (i.e. prairie region) to model how species relative abundance differed among soil types and human footprint types for the prairie region only. A coarse index of habitat use may be available for the forested region.</div>',
      Basic: '<div style="max-width: 200px"><span class="summary-icon basic">Basic Summary:</span> For species with few detections in both the northern analysis unit (forested region) and southern analysis unit (prairie region), a coarse index of habitat use may be presented along with the species occurrence map.</div>',
      OSR: `
      <div style="max-width: 300px">
      <span class="summary-icon osr">Oil Sands Region Summary:</span>
      The ABMI modelled how species relative abundance was predicted to change in response to energy-specific human footprint, such as seismic lines and well sites, in the Oil Sands Region.
      </div>
      `
    }
  },
  habitat: {
    title: 'HABITAT ELEMENTS'
  },
  common: {
    or: 'Or',
    beginWith: 'Begin With ...',
    select: 'Select',
    load: {
      loading: 'Loading ...',
      noResult: 'No Matching Record ...',
      noMoreResult: ''
    }
  },
  autoComplete: {
    enterMoreCharacter: 'Enter at least {characterCount} characters to start species search'
  }
}
